import { ROLE_PERMISSIONS, type Permission } from '@/shared/constants/permissions';
import type { UUID } from '@/shared/types/api';

import type { AccessGrant, MyAccess, ScopeChain } from '../types/access.types';

/**
 * Client-side mirror of `AuthorizationService.has_permission`.
 *
 * This decides what the UI *offers*. The backend independently decides what is
 * *allowed* — every endpoint re-runs the same check. Divergence here can only
 * hide an action the user could have taken, or surface a 403 they should not
 * have been offered; it can never grant access.
 */

/** Permissions a grant confers, from the role→permission table. */
function permissionsOf(grant: AccessGrant): readonly Permission[] {
  return ROLE_PERMISSIONS[grant.role_name] ?? [];
}

/**
 * Does this grant cover the given resource? A grant applies when it sits at or
 * above the resource in the `system > complex > building > unit` hierarchy.
 */
function grantCoversScope(grant: AccessGrant, scope: ScopeChain): boolean {
  switch (grant.scope_type) {
    case 'system':
      return true;
    case 'complex':
      return Boolean(scope.complexId) && grant.scope_id === scope.complexId;
    case 'building':
      return Boolean(scope.buildingId) && grant.scope_id === scope.buildingId;
    case 'unit':
      return Boolean(scope.unitId) && grant.scope_id === scope.unitId;
    default:
      return false;
  }
}

export function hasPermission(
  access: MyAccess | null | undefined,
  permission: Permission,
  scope: ScopeChain = {},
): boolean {
  if (!access) return false;
  // The system-wide role short-circuits every scope comparison, exactly as it
  // does server-side.
  if (access.is_superadmin) return true;

  return access.roles.some(
    (grant) => permissionsOf(grant).includes(permission) && grantCoversScope(grant, scope),
  );
}

/** True when the user holds `permission` *somewhere* — used to reveal sections. */
export function hasPermissionAnywhere(
  access: MyAccess | null | undefined,
  permission: Permission,
): boolean {
  if (!access) return false;
  if (access.is_superadmin) return true;
  return access.roles.some((grant) => permissionsOf(grant).includes(permission));
}

/** The user lives in this unit. Residency is implicit access, not a role. */
export function isUnitMember(access: MyAccess | null | undefined, unitId: UUID): boolean {
  return Boolean(access?.memberships.some((membership) => membership.unit_id === unitId));
}

/** Ids of every scope of a kind the user holds a grant on, deduplicated. */
export function scopeIdsOfType(
  access: MyAccess | null | undefined,
  scopeType: AccessGrant['scope_type'],
): UUID[] {
  if (!access) return [];
  const ids = new Set<UUID>();
  for (const grant of access.roles) {
    if (grant.scope_type === scopeType && grant.scope_id) ids.add(grant.scope_id);
  }
  return [...ids];
}

/** Any management grant at all, ignoring which one. */
export function isManager(access: MyAccess | null | undefined): boolean {
  return Boolean(access && (access.is_superadmin || access.roles.length > 0));
}

export function isResident(access: MyAccess | null | undefined): boolean {
  return Boolean(access && access.memberships.length > 0);
}

/**
 * Which landing experience the user's access implies.
 *
 * Manager outranks resident when both apply: a manager who also lives in a unit
 * still lands on the dashboard, and reaches their own charges from there. A
 * `newcomer` has neither and must go through onboarding.
 */
export function accessProfileOf(
  access: MyAccess | null | undefined,
): 'superadmin' | 'manager' | 'resident' | 'newcomer' {
  if (!access) return 'newcomer';
  if (access.is_superadmin) return 'superadmin';
  if (access.roles.length > 0) return 'manager';
  if (access.memberships.length > 0) return 'resident';
  return 'newcomer';
}
